import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";
import type { ApAttachment, ApInvoice } from "@/accounts-payable/types";
import { fileKind, formatFileSize } from "@/accounts-payable/utils";
import { ExtractedInvoiceRow } from "./extracted-invoice-row";
import {
  fileIconFor,
  missingInvoicesMessage,
  NO_INVOICE_OUTCOME,
} from "./utils";

const Note = ({
  children,
  negative = false,
}: {
  children: string;
  negative?: boolean;
}) => (
  // Same vertical padding as an invoice row, so a note lines up the same way.
  <Typography
    variant="captionW1"
    textColor={negative ? "negative" : "tertiary"}
    className="py-2"
  >
    {children}
  </Typography>
);

/**
 * One email attachment: a file icon and name, then (indented on a thin rule,
 * to read as coming *from* the file) the invoices extracted from it (one
 * attachment can yield several, e.g. a statement), or a line saying why there
 * are none.
 */
export const EmailAttachmentRow = ({
  attachment,
  invoices,
  invoicesLoading,
  receiptsEnabled = true,
}: {
  attachment: ApAttachment;
  /** Every invoice loaded for the email; filtered to this attachment here. */
  invoices: ApInvoice[];
  invoicesLoading: boolean;
  /** Whether the business processes sales receipts (see `useReceiptsEnabled`). */
  receiptsEnabled?: boolean;
}) => {
  const matchedInvoices = invoices.filter((i) =>
    attachment.invoice_ids.includes(i.id),
  );
  const expected = attachment.invoice_ids.length;
  const outcome =
    attachment.status === "is_receipt" && !receiptsEnabled
      ? { text: "A sales receipt. Receipts are turned off for this business." }
      : NO_INVOICE_OUTCOME[attachment.status];
  const Icon = fileIconFor(attachment.content_type);
  const missingMessage = missingInvoicesMessage(
    expected,
    matchedInvoices.length,
    invoicesLoading,
  );
  // Some files arrive without a size; show just the type then, not "· —".
  const size = attachment.file_size
    ? ` · ${formatFileSize(attachment.file_size)}`
    : "";

  return (
    <Stack direction="row" align="start" gap="sm" fullWidth>
      <Icon
        className="h-5 w-5 flex-none text-tertiary-text"
        strokeWidth={1.5}
        aria-hidden
      />
      <Stack gap="sm" fullWidth className="min-w-0">
        <Stack gap="none" fullWidth>
          <Typography variant="subtitleW3" className="break-all">
            {attachment.file_name}
          </Typography>
          <Typography variant="captionW1" textColor="tertiary">
            {fileKind(attachment.content_type)}
            {size}
          </Typography>
        </Stack>

        {/* align="stretch" so each invoice row spans the width (amount on the
            right, hover fill edge to edge) instead of shrink-wrapping. */}
        <Stack
          gap="none"
          align="stretch"
          fullWidth
          className="border-l-2 border-stroke-tertiary pl-3"
        >
          {/* Branch on invoice_ids, not the fetch results: "Not an invoice"
              is about classification and must never show for an attachment
              that produced invoices. */}
          {expected === 0 ? (
            <Note negative={outcome.negative}>{outcome.text}</Note>
          ) : (
            <>
              {matchedInvoices.map((invoice) => (
                <ExtractedInvoiceRow key={invoice.id} invoice={invoice} />
              ))}
              {missingMessage && <Note>{missingMessage}</Note>}
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
